import { AssignedUsers } from './assigned-users';
import { ActionCellRenderer } from '../../../../../utils/custom-renderer/action-cell-renderer';
import { redUnassignUserIcon } from '../../../../../styles';
import { getGroupColumnSameObject } from '../../../../../utils/grid';
import moment from 'moment';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../../utils/custom-renderer/hyperlink-cell-renderer';
import { ICONS } from '../../../../../utils';

export enum COLUMN_FIELDS {
  STATUS = 'STATUS',
  USER_NAME = 'USER_NAME',
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  ROLE = 'ROLE',
  ADVISOR_COMPANY_NAME = 'ADVISOR_COMPANY_NAME',
  EMAIL_ADDRESS = 'EMAIL_ADDRESS',
  TELEPHONE_NUMBER_DIRECT = 'TELEPHONE_NUMBER_DIRECT',
  DATE_ADDED = 'DATE_ADDED',
  UNASSIGN_USER = 'UNASSIGN_USER',
}

export function COLUMNS_CONFIG(context: AssignedUsers) {
  return [
    {
      headerName: '',
      suppressMenu: true,
      headerCheckboxSelection: true,
      width: 50,
      checkboxSelection: () => {
        return !context.company?.IS_SPV_FLAG;
      },
    },
    {
      field: COLUMN_FIELDS.STATUS,
      width: 65,
      headerName: 'Status',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      suppressRowClickSelection: true,
      valueGetter: (params) => {
        const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];
        if (params.data) {
          if (params.data.COMPANIES_LINKED > 1 && params.data.STATUS === 'ENABLED') {
            iconList.push({
              icon: ICONS.CIRCLED_WARNING,
              width: '16px',
              height: '18px',
            });
          } else if (params.data.STATUS === 'ENABLED') {
            iconList.push({
              icon: ICONS.GREEN_CHECK,
              width: '16px',
              height: '18px',
            });
          } else {
            iconList.push({
              icon: ICONS.CIRCLED_ERROR,
              width: '16px',
              height: '18px',
            });
          }

          return JSON.stringify({
            STATUS: params.data.STATUS,
            COMPANIES_LINKED: params.data.COMPANIES_LINKED,
            NAME: ' ',
            ICON_LIST: iconList,
          });
        }
      },
      cellStyle: { 'justify-content': 'center', 'align-items': 'center' },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        if (!params.value && !params.data) return;

        const obj = JSON.parse(params.value);
        const hyperlinkParams: IGridHyperlink = {
          value: obj?.NAME,
          id: '',
          vppaId: null,
          iconList: obj?.ICON_LIST,
        };

        return hyperlinkParams;
      },
    },
    {
      field: COLUMN_FIELDS.USER_NAME,
      headerName: 'Username',
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      suppressRowClickSelection: true,
    },
    {
      field: COLUMN_FIELDS.FIRST_NAME,
      headerName: 'First Name',
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      suppressRowClickSelection: true,
    },
    {
      field: COLUMN_FIELDS.LAST_NAME,
      headerName: 'Last Name',
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      suppressRowClickSelection: true,
    },
    {
      field: COLUMN_FIELDS.ROLE,
      headerName: 'Role',
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      suppressRowClickSelection: true,
      valueGetter: (params) => {
        return context.profileUsers
          .filter((x) => x.USER_NAME === params.data.USER_NAME)
          .map((obj) => obj.PROFILE_NAME)
          .join(', ');
      },
    },
    {
      field: COLUMN_FIELDS.ADVISOR_COMPANY_NAME,
      headerName: 'Advisor Company',
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      suppressRowClickSelection: true,
      valueGetter: (params) => {
        return params.data.ADVISOR_COMPANY_NAME;
      },
    },
    {
      field: COLUMN_FIELDS.EMAIL_ADDRESS,
      headerName: 'Email Address',
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      suppressRowClickSelection: true,
    },
    {
      field: COLUMN_FIELDS.TELEPHONE_NUMBER_DIRECT,
      headerName: 'Primary Contact',
      flex: 1,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      suppressRowClickSelection: true,
    },
    {
      field: COLUMN_FIELDS.DATE_ADDED,
      headerName: 'Date Added',
      flex: 1,
      sortable: true,
      filter: 'agDateColumnFilter',
      menuTabs: ['filterMenuTab'],
      filterParams: {
        comparator: (filterLocalDateAtMidnight: Date, cellValue) => {
          const date = new Date(
            Date.UTC(
              filterLocalDateAtMidnight.getFullYear(),
              filterLocalDateAtMidnight.getMonth(),
              filterLocalDateAtMidnight.getDate()
            )
          );

          if (moment(cellValue).isBefore(date, 'day')) {
            return -1;
          } else if (moment(cellValue).isAfter(date, 'day')) {
            return 1;
          }
          return 0;
        },
        browserDatePicker: true,
        buttons: ['apply', 'reset'],
      },
      suppressRowClickSelection: true,
    },
    {
      field: COLUMN_FIELDS.UNASSIGN_USER,
      headerName: '',
      pinned: 'right',
      suppressMenu: true,
      width: 55,
      hide: !context.showAssignedUsers,
      cellRenderer: ActionCellRenderer,
      cellRendererParams: (params) => {
        const unassignUserBtn = {
          name: redUnassignUserIcon('start'),
          callback: () => {
            context.unassignUser(getGroupColumnSameObject(params, COLUMN_FIELDS.USER_NAME));
          },
          actionName: 'unassign-user',
          disabled: context.company?.IS_SPV_FLAG,
        };

        const actions = [unassignUserBtn];
        return {
          actions,
          enabled: true,
        };
      },
    },
  ];
}
